import React, { useState, useEffect } from 'react';
import { HiMicrophone, HiPencil, HiTrash, HiPlay, HiGlobe, HiPlus, HiUpload, HiX, HiSearch, HiCog, HiHeart } from 'react-icons/hi';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AppLoadingState from './common/AppLoadingState';
import { AppPageShell, Button, PageHeader } from './common/ui';

const Voices = () => {
  // State management
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('curated');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('none');
  const [openCloneDialog, setOpenCloneDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [audioSamples, setAudioSamples] = useState([]);
  const [newVoiceName, setNewVoiceName] = useState('');
  const [newVoiceDescription, setNewVoiceDescription] = useState('');
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Global voice sample settings
  const [globalSampleText, setGlobalSampleText] = useState('Hello, this is a sample of my voice.');

  // Fetch voices on component mount
  useEffect(() => {
    fetchVoices();
  }, []);

  const fetchVoices = async () => {
    try {
      setLoading(true);
      const response = await api.getVoices();
      setVoices(response.voices || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (voice) => {
    try {
      if (voice.is_favorite) {
        await api.removeVoiceFromFavorites(voice.id);
        setVoices(voices.map(v => 
          v.id === voice.id ? { ...v, is_favorite: false } : v
        ));
        toast.success('Voice removed from favorites');
      } else {
        await api.addVoiceToFavorites(voice.id, voice.name);
        setVoices(voices.map(v => 
          v.id === voice.id ? { ...v, is_favorite: true } : v
        ));
        toast.success('Voice added to favorites');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Sort voices based on current sort order
  const sortVoices = (voicesToSort) => {
    switch (sortOrder) {
      case 'rating-high':
        return [...voicesToSort].sort((a, b) => {
          const ratingA = a.average_rating || 0;
          const ratingB = b.average_rating || 0;
          if (ratingA === ratingB) {
            // If ratings are equal, sort by total number of ratings
            return (b.total_ratings || 0) - (a.total_ratings || 0);
          }
          return ratingB - ratingA;
        });
      case 'rating-low':
        return [...voicesToSort].sort((a, b) => {
          const ratingA = a.average_rating || 0;
          const ratingB = b.average_rating || 0;
          if (ratingA === ratingB) {
            // If ratings are equal, sort by total number of ratings (still descending)
            return (b.total_ratings || 0) - (a.total_ratings || 0);
          }
          return ratingA - ratingB;
        });
      default:
        return voicesToSort;
    }
  };

  // Filter voices based on tab and search query
  const filteredVoices = sortVoices(voices.filter(voice => {
    const matchesSearch = (voice.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (voice.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (voice.tags || []).some(tag => (tag || '').toLowerCase().includes(searchQuery.toLowerCase()));

    switch (activeTab) {
      case 'curated':
        return matchesSearch && voice.tags?.includes('Curated by us');
      case 'your':
        return matchesSearch && voice.is_user_voice;
      case 'favorites':
        return matchesSearch && voice.is_favorite;
      case 'all':
        return matchesSearch && voice.public;
      default:
        return false;
    }
  }));

  const handleCloneVoice = async () => {
    try {
      if (!newVoiceName || audioSamples.length === 0) {
        toast.error('Please provide a name and audio samples');
        return;
      }

      setLoading(true);
      await api.cloneVoice(newVoiceName, audioSamples, newVoiceDescription);
      toast.success('Voice cloned successfully');
      fetchVoices();
      handleCloseCloneDialog();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRenameVoice = async () => {
    try {
      if (!newVoiceName || !selectedVoice) return;
      
      setLoading(true);
      await api.renameVoice(selectedVoice.id, newVoiceName, newVoiceDescription);
      toast.success('Voice updated successfully');
      fetchVoices();
      handleCloseRenameDialog();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVoice = async (voice) => {
    if (!window.confirm(`Are you sure you want to delete the voice "${voice.name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await api.deleteVoice(voice.id);
      // Refresh the voices list
      await fetchVoices();
      toast.success('Voice deleted successfully');
    } catch (error) {
      console.error('Error deleting voice:', error);
      toast.error(error.message || 'Failed to delete voice');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSample = async (voice) => {
    try {
      if (!globalSampleText || audioPlaying) return;
      
      setLoading(true);
      setAudioPlaying(true);
      
      const audioBlob = await api.generateVoiceSample(
        voice.id,
        globalSampleText,
        null,
        // Voice sample uses the same language codes as POST /calls (provider docs); not legacy "ENG".
        'en-US'
      );

      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.play();
      
      audio.onended = () => {
        setAudioPlaying(false);
        URL.revokeObjectURL(audioUrl);
        setLoading(false);
      };

    } catch (err) {
      toast.error(err.message);
      setAudioPlaying(false);
      setLoading(false);
    }
  };

  // Dialog handlers
  const handleOpenCloneDialog = () => {
    setOpenCloneDialog(true);
    setNewVoiceName('');
    setNewVoiceDescription('');
    setAudioSamples([]);
  };

  const handleCloseCloneDialog = () => {
    setOpenCloneDialog(false);
    setNewVoiceName('');
    setNewVoiceDescription('');
    setAudioSamples([]);
  };

  const handleOpenRenameDialog = (voice) => {
    setSelectedVoice(voice);
    setNewVoiceName(voice.name);
    setNewVoiceDescription(voice.description || '');
    setOpenRenameDialog(true);
  };

  const handleCloseRenameDialog = () => {
    setOpenRenameDialog(false);
    setSelectedVoice(null);
    setNewVoiceName('');
    setNewVoiceDescription('');
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setAudioSamples(files);
  };

  if (loading && voices.length === 0) {
    return <AppLoadingState layout="section" showLabel={false} />;
  }

  return (
    <AppPageShell maxWidthClass="max-w-7xl">
      <PageHeader
        eyebrow="Resources"
        title="Voice management"
        description="Browse curated and custom voices, preview samples, and clone new speakers for your agents."
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" onClick={() => setShowSettings(!showSettings)}>
              <HiCog className="-ml-1 mr-2 h-5 w-5" />
              Sample settings
            </Button>
            <Button onClick={handleOpenCloneDialog}>
              <HiPlus className="-ml-1 mr-2 h-5 w-5" />
              Clone new voice
            </Button>
          </div>
        }
      />

      {/* Global Sample Settings */}
      {showSettings && (
        <div className="mb-6 bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Voice Sample Settings</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sample Text
            </label>
            <textarea
              rows="2"
              value={globalSampleText}
              onChange={(e) => setGlobalSampleText(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter text to be spoken by the voice..."
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'curated', name: 'Curated Voices' },
            { id: 'your', name: 'Your Voices' },
            { id: 'favorites', name: 'Favorites' },
            { id: 'all', name: 'All Voices' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Search and Sort */}
      <div className="mb-6 flex items-center justify-between">
        <div className="relative flex-1 max-w-lg">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <HiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search Voices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
        <div className="ml-4">
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          >
            <option value="none">Sort by</option>
            <option value="rating-high">Highest Rated</option>
            <option value="rating-low">Lowest Rated</option>
          </select>
        </div>
      </div>

      {/* Voice List */}
      <div className="space-y-4">
        {filteredVoices.map((voice) => (
          <div key={voice.id} className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <HiMicrophone className="h-6 w-6 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{voice.name}</h3>
                    <p className="text-sm text-gray-500">{voice.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  {voice.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleFavorite(voice)}
                      className={`p-2 ${
                        voice.is_favorite
                          ? 'text-red-500 hover:text-red-600'
                          : 'text-gray-400 hover:text-gray-500'
                      }`}
                      title={voice.is_favorite ? "Remove from favorites" : "Add to favorites"}
                    >
                      <HiHeart className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleGenerateSample(voice)}
                      disabled={audioPlaying || !globalSampleText}
                      className="p-2 text-gray-400 hover:text-gray-500 disabled:opacity-50"
                      title={!globalSampleText ? "Please set sample text in settings" : "Generate Sample"}
                    >
                      <HiPlay className="h-5 w-5" />
                    </button>
                    {voice.is_user_voice && (
                      <>
                        <button
                          onClick={() => handleOpenRenameDialog(voice)}
                          className="p-2 text-gray-400 hover:text-gray-500"
                          title="Rename"
                        >
                          <HiPencil className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteVoice(voice)}
                          className="p-2 text-gray-400 hover:text-gray-500"
                          title="Delete"
                        >
                          <HiTrash className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {voice.average_rating > 0 && (
                <div className="mt-2 flex items-center">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`h-4 w-4 ${
                          i < Math.round(voice.average_rating || 0)
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 15.585l-6.327 3.323 1.209-7.037L.172 7.332l7.046-1.024L10 0l2.782 6.308 7.046 1.024-4.71 4.539 1.209 7.037z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ))}
                  </div>
                  <p className="ml-2 text-sm text-gray-500">
                    {(voice.average_rating || 0).toFixed(1)} ({voice.total_ratings || 0} ratings)
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Clone Voice Dialog */}
      {openCloneDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={handleCloseCloneDialog}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <HiX className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Clone New Voice</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Voice Name *
                      </label>
                      <input
                        type="text"
                        placeholder="Voice Name"
                        value={newVoiceName}
                        onChange={(e) => setNewVoiceName(e.target.value)}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        placeholder="Enter a description for your voice"
                        value={newVoiceDescription}
                        onChange={(e) => setNewVoiceDescription(e.target.value)}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        rows="3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Audio Samples *</label>
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          <HiUpload className="mx-auto h-12 w-12 text-gray-400" />
                          <div className="flex text-sm text-gray-600">
                            <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                              <span>Upload files</span>
                              <input
                                type="file"
                                multiple
                                accept="audio/*"
                                onChange={handleFileChange}
                                className="sr-only"
                              />
                            </label>
                          </div>
                          {audioSamples.length > 0 && (
                            <p className="text-xs text-gray-500">
                              {audioSamples.length} file(s) selected
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloneVoice}
                  disabled={!newVoiceName || audioSamples.length === 0 || loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Clone
                </button>
                <button
                  type="button"
                  onClick={handleCloseCloneDialog}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rename Voice Dialog */}
      {openRenameDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={handleCloseRenameDialog}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <HiX className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Update Voice</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Voice Name *
                      </label>
                      <input
                        type="text"
                        placeholder="New Name"
                        value={newVoiceName}
                        onChange={(e) => setNewVoiceName(e.target.value)}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        placeholder="Enter a description for your voice"
                        value={newVoiceDescription}
                        onChange={(e) => setNewVoiceDescription(e.target.value)}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleRenameVoice}
                  disabled={!newVoiceName || loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={handleCloseRenameDialog}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppPageShell>
  );
};

export default Voices; 